import React, { useState, useRef, useMemo } from 'react';
import { Form, Input, InputNumber, Button, Row, Col } from 'antd';
import { useDispatch, useSelector } from 'react-redux';
import PropTypes from 'prop-types';
import { UilArrowLeft } from '@iconscout/react-unicons';
import { createPayment, setPaymentSlug } from '../../redux/payment/actions';
import Loader from '../loader/Loader';
import { setTransparentLoader } from '../../redux/loader/loaderAction';

import ALMA_LOGO from '../../assets/logo/alma.svg';
import SCALAPAY_LOGO from '../../assets/logo/scalapay.svg';
import KLARNA_LOGO from '../../assets/logo/klarna.svg';

const paymentMethodLogos = {
  alma: ALMA_LOGO,
  scalapay: SCALAPAY_LOGO,
  klarna: KLARNA_LOGO,
};

function PaymentForm({ btnColour, slug, language, businessData }) {
  PaymentForm.propTypes = {
    btnColour: PropTypes.string, // Ensures btnColour is a string
    language: PropTypes.object, // Ensures btnColour is a string
    businessData: PropTypes.object.isRequired,
    slug: PropTypes.string.isRequired, // Ensures slug is a required string
  };
  const dispatch = useDispatch();
  const createPaymentLoader = useSelector((state) => state.payment.createPaymentLoader);
  // const paymentType = useSelector((state) => state.payment.userPaymentDetails.payment_type);

  const [submitType, setSubmitType] = useState('');
  const [paymentType, setPaymentType] = useState('one_time');
  // const [formType, setFormType] = useState('');
  const [subscriptionType, setsubscriptionType] = useState('month');
  const [paymentMethod, setelectPaymentMethod] = useState('');
  const [formData, setFormData] = useState();
  const [showOtherInstallmentMethods, setShowOtherInstallmentMethods] = useState(false);

  const [paymentTypeMethod, setSubmitTypeMethod] = useState('');
  const loaderStatus = useSelector((state) => state.loader.transparentLoader);
  const [formValues, setFormValues] = useState({
    remember: true,
  });
  const [form] = Form.useForm();
  const installmentChoiceRef = useRef('');
  /** Same-tick as form submit; `submitType` state can still be stale in `onFinish`. */
  const pendingSubmitTypeRef = useRef('');

  const stripeConnected = businessData?.stripe_status === 'connected';
  const almaConnected = businessData?.alma_onboarding_status === 'connected';

  const installmentMethods = useMemo(() => {
    const raw = businessData?.stripe_installment_payment_methods;
    return Array.isArray(raw) ? raw.filter(Boolean) : [];
  }, [businessData?.stripe_installment_payment_methods]);

  const paymentFeatureSlugs = useMemo(() => {
    const list = businessData?.payment_features;
    if (!Array.isArray(list)) return new Set();
    return new Set(list.map((f) => f?.slug).filter(Boolean));
  }, [businessData?.payment_features]);

  const showInstallmentButton = useMemo(() => {
    if (!installmentMethods.length) return false;
    return installmentMethods.some((m) => {
      if (m === 'klarna' || m === 'scalapay') return stripeConnected;
      if (m === 'alma') return almaConnected;
      return stripeConnected;
    });
  }, [installmentMethods, stripeConnected, almaConnected]);

  const showPayNowButton = stripeConnected && paymentFeatureSlugs.has('pay_now');
  const showSubscribeButton = stripeConnected && paymentFeatureSlugs.has('subscription');
  const hasScalapay = installmentMethods.includes('scalapay');
  const prioritizedInstallmentMethods = useMemo(() => {
    if (!hasScalapay) return installmentMethods;
    return ['scalapay', ...installmentMethods.filter((method) => method !== 'scalapay')];
  }, [hasScalapay, installmentMethods]);
  const otherInstallmentMethods = useMemo(
    () =>
      hasScalapay
        ? prioritizedInstallmentMethods.filter((method) => method !== 'scalapay')
        : prioritizedInstallmentMethods,
    [hasScalapay, prioritizedInstallmentMethods],
  );

  const formatInstallmentLabel = (method) => {
    if (!method || typeof method !== 'string') return '';
    return method.charAt(0).toUpperCase() + method.slice(1);
  };

  const renderPaymentMethodLabel = (method, isActive = false) => {
    const normalizedMethod = method?.toLowerCase?.() || '';
    const logo = paymentMethodLogos[normalizedMethod];

    if (logo) {
      return (
        <span
          style={{
            display: 'inline-block',
            width: normalizedMethod === 'scalapay' ? '180px' : '150px',
            height: '100%',
            backgroundColor: isActive ? '#ffffff' : btnColour || '#1677ff',
            WebkitMaskImage: `url(${logo})`,
            maskImage: `url(${logo})`,
            WebkitMaskRepeat: 'no-repeat',
            maskRepeat: 'no-repeat',
            WebkitMaskPosition: 'center',
            maskPosition: 'center',
            WebkitMaskSize: 'contain',
            maskSize: 'contain',
            verticalAlign: 'middle',
          }}
          aria-label={normalizedMethod}
        />
      );
    }

    if (typeof method !== 'string' || method.length === 0) {
      return '';
    }

    return method.charAt(0).toUpperCase() + method.slice(1);
  };

  const onFinish = (values) => {
    setFormValues(values);
    const merged = { ...(formData || {}), ...values };
    const methodFromStep =
      paymentType === 'payment_method' ? installmentChoiceRef.current || paymentMethod : paymentMethod;

    const effectiveSubmitType = pendingSubmitTypeRef.current || submitType;
    pendingSubmitTypeRef.current = '';

    setSubmitTypeMethod(effectiveSubmitType);

    dispatch(setPaymentSlug(slug));

    if (effectiveSubmitType === 'one_time') {
      dispatch(
        createPayment({
          ...merged,
          payment_type: 'one_time',
          payment_method_type: paymentMethod || 'stripe',
          slug,
        }),
      );
      return;
    }

    if (effectiveSubmitType === 'subscription' && merged.duration != null && merged.duration !== '') {
      dispatch(
        createPayment({
          ...merged,
          payment_type: 'subscription',
          duration_type: subscriptionType,
          payment_method_type: paymentMethod || 'stripe',
          slug,
        }),
      );
      return;
    }

    if (effectiveSubmitType === 'installment' && methodFromStep) {
      const payload = {
        ...merged,
        payment_type: 'installment',
        slug,
      };
      if (methodFromStep === 'klarna' || methodFromStep === 'scalapay') {
        payload.payment_method_type = 'stripe';
        payload.installment_pay_method = methodFromStep;
      } else if (methodFromStep === 'alma') {
        payload.payment_method_type = 'alma';
      } else {
        payload.payment_method_type = methodFromStep;
      }
      dispatch(createPayment(payload));
      return;
    }

    if (methodFromStep === 'klarna' || methodFromStep === 'scalapay' || methodFromStep === 'alma') {
      const payload = {
        ...merged,
        payment_type: 'installment',
        slug,
      };
      if (methodFromStep === 'klarna' || methodFromStep === 'scalapay') {
        payload.payment_method_type = 'stripe';
        payload.installment_pay_method = methodFromStep;
      } else {
        payload.payment_method_type = methodFromStep;
      }
      dispatch(createPayment(payload));
    }
  };
  const onFinishFailed = (errorInfo) => {
    console.log('Failed:', errorInfo);
  };

  const handleInstallment = () => {
    if (!installmentMethods.length) return;
    installmentChoiceRef.current = '';
    setShowOtherInstallmentMethods(false);
    pendingSubmitTypeRef.current = 'installment';
    setelectPaymentMethod('');
    setSubmitType('installment');
    setSubmitTypeMethod('installment');
    dispatch(setTransparentLoader(true));
    setTimeout(() => {
      dispatch(setTransparentLoader(false));
      setPaymentType('payment_method');
    }, 500);
  };

  console.log('installmentMethods', installmentMethods);

  return (
    <>
      {' '}
      {loaderStatus && <Loader />}
      {paymentType === 'one_time' ? (
        <>
          {' '}
          <Form
            form={form}
            className="user-details-form"
            name="basic"
            layout="vertical"
            initialValues={{ ...formValues }} // Use the stored values as initial values
            onFinish={onFinish}
            onFinishFailed={onFinishFailed}
          >
            <>
              <Row gutter={16}>
                <Col xs={24} md={12}>
                  <Form.Item
                    label={language?.content?.NAME}
                    name="name"
                    rules={[{ required: true, message: language?.message?.error?.ERR053 }]}
                  >
                    <Input placeholder={language?.content?.ENTER_NAME_PLACEHOLDER} type="text" maxLength={50} />
                  </Form.Item>
                </Col>

                <Col xs={24} md={12}>
                  <Form.Item
                    label={language?.content?.EMAIL_ADDRESS}
                    name="email"
                    type="email"
                    rules={[
                      {
                        required: true,
                        message: language?.message?.error?.ERR018,
                        validateTrigger: 'onSubmit',
                      },
                      () => ({
                        validator(_, value) {
                          const emailRegex = /^[a-zA-Z0-9_.+-]+@[a-zA-Z0-9-]+\.[a-zA-Z0-9-.]+$/;
                          if (value && !emailRegex.test(value)) {
                            return Promise.reject(new Error(language?.message?.error?.ERR019));
                          }
                          return Promise.resolve();
                        },
                      }),
                    ]}
                  >
                    <Input type="email" placeholder={language?.content?.EMAIL_ADDRESS_PLACEHOLDER} maxLength={50} />
                  </Form.Item>
                </Col>
              </Row>
              <Row gutter={16}>
                <Col xs={24} md={12}>
                  <Form.Item
                    label={language?.content?.PAYMENT_DESCRIPTION}
                    name="description"
                    rules={[{ required: true, message: language?.message?.error?.ERR054 }]}
                  >
                    <Input
                      placeholder={language?.content?.PAYMENT_DESCRIPTION_PLACEHOLDER}
                      type="text"
                      maxLength={100}
                    />
                  </Form.Item>
                </Col>

                <Col xs={24} md={12} className="custom-height">
                  <Form.Item
                    label={language?.content?.AMOUNT}
                    name="amount"
                    rules={[{ required: true, message: language?.message?.error?.ERR055 }]}
                  >
                    <InputNumber
                      placeholder={language?.content?.AMOUNT_PLACEHOLDER}
                      min={1}
                      maxLength={7}
                      step={100}
                      formatter={(value) => `€ ${value}`}
                      parser={(value) => value.replace(/€\s?|(,*)/g, '')}
                      style={{ width: '100%' }}
                    />
                  </Form.Item>
                </Col>
              </Row>
              <Row gutter={[16, 16]} className="pt-2">
                {/* {businessData?.alma_onboarding_status === 'connected' ||
                  (businessData?.stripe_status === 'connected' &&
                    businessData?.stripe_installment_payment_methods.length > 0 && (
                      <>
                        <Col xs={24} md={10}>
                          <Button
                            type="default"
                            className="warning-button"
                            style={{
                              background: btnColour || '#fc8600',
                              border: btnColour || '#fc8600',
                              fontWeight: '500',
                              fontSize: '16px',
                            }}
                            block
                            // onClick={() => setSubmitType('installment')}
                            onClick={() => handleInstallment()}
                            htmlType="submit"
                          >
                            {(createPaymentLoader || loaderStatus) && paymentTypeMethod === 'installment'
                              ? language?.content?.LOADING
                              : language?.content?.IN_INSTALLMENT}{' '}
                          </Button>
                        </Col>
                      </>
                    ))} */}
                {showInstallmentButton && (
                  <Col xs={24} md={10}>
                    <Button
                      type="default"
                      className="warning-button"
                      style={{
                        background: btnColour || '#fc8600',
                        border: btnColour || '#fc8600',
                        fontWeight: '500',
                        fontSize: '16px',
                      }}
                      block
                      onClick={async () => {
                        try {
                          const values = await form.validateFields();
                          setFormData(values);
                          handleInstallment();
                        } catch (err) {
                          // validation failed → stay here
                        }
                      }}
                      htmlType="button"
                    >
                      {(createPaymentLoader || loaderStatus) && paymentTypeMethod === 'installment'
                        ? language?.content?.LOADING
                        : language?.content?.IN_INSTALLMENT}{' '}
                    </Button>
                  </Col>
                )}

                {(showPayNowButton || showSubscribeButton) && (
                  <>
                    {showPayNowButton && (
                      <Col xs={12} md={7}>
                        <Button
                          type="default"
                          style={{
                            borderColor: btnColour || '#fc8600',
                            color: btnColour || '#000',
                            height: '50px',
                            fontWeight: '500',
                            fontSize: '16px',
                          }}
                          block
                          onClick={() => {
                            pendingSubmitTypeRef.current = 'one_time';
                            setSubmitType('one_time');
                            setelectPaymentMethod('stripe');
                          }}
                          htmlType="submit"
                        >
                          {createPaymentLoader && paymentTypeMethod === 'one_time'
                            ? language?.content?.LOADING
                            : language?.content?.NOW}
                        </Button>
                      </Col>
                    )}
                    {showSubscribeButton && (
                      <Col xs={12} md={7}>
                        <Button
                          type="default"
                          block
                          style={{
                            borderColor: btnColour || '#fc8600',
                            color: btnColour || '#000',
                            height: '50px',
                            fontWeight: '500',
                            fontSize: '16px',
                          }}
                          onClick={async () => {
                            try {
                              const values = await form.validateFields();

                              setFormData(values);
                              setSubmitType('subscription');
                              setPaymentType('subscription');
                              setelectPaymentMethod('stripe');
                            } catch (error) {
                              // validation error → stay on same step
                            }
                          }}
                        >
                          {language?.content?.SUBSCRIBE || 'Subscribe'}
                        </Button>
                      </Col>
                    )}
                  </>
                )}
              </Row>
            </>
          </Form>
        </>
      ) : paymentType === 'payment_method' ? (
        <Form
          className="user-details-form"
          name="basic"
          layout="vertical"
          initialValues={{ remember: true }}
          onFinish={onFinish}
          onFinishFailed={onFinishFailed}
        >
          <>
            <div style={{ width: '100%' }}>
              <div style={{ width: '100%', maxWidth: '320px', marginLeft: 0 }}>
                <Row className="mb-20">
                  <Col xs={24} className="text-center">
                    <div
                      style={{
                        fontSize: '18px',
                        fontWeight: '500',
                        color: '#1f1f2f',
                        textAlign: 'center',
                      }}
                    >
                      {language?.content?.PAY_VIA || 'Pay via'}
                    </div>
                  </Col>
                </Row>
                <Row gutter={[24, 24]} className="mb-20">
                  {hasScalapay ? (
                    <>
                      <Col xs={24} md={24}>
                        <Button
                          type="default"
                          style={{
                            background: btnColour,
                            color: '#ffffff',
                            border: `1px solid ${btnColour}`,
                            height: '50px',
                            fontWeight: '500',
                            fontSize: '16px',
                            width: '100%',
                            maxWidth: '420px',
                            margin: '0 auto',
                            display: 'block',
                          }}
                          onClick={() => {
                            installmentChoiceRef.current = 'scalapay';
                            pendingSubmitTypeRef.current = 'installment';
                            setelectPaymentMethod('scalapay');
                            setSubmitType('installment');
                          }}
                          htmlType="submit"
                        >
                          {renderPaymentMethodLabel('scalapay', true)}
                        </Button>
                      </Col>

                      {!showOtherInstallmentMethods && otherInstallmentMethods.length > 0 && (
                        <Col xs={24} md={24} className="text-center">
                          <Button
                            type="link"
                            onClick={() => setShowOtherInstallmentMethods(true)}
                            style={{
                              color: btnColour,
                              fontWeight: '500',
                              fontSize: '16px',
                              padding: 0,
                              display: 'inline-flex',
                              justifyContent: 'center',
                            }}
                          >
                            {language?.content?.OTHER_PAYMENT_METHODS || 'Other Payment Methods'}
                          </Button>
                        </Col>
                      )}

                      {showOtherInstallmentMethods &&
                        otherInstallmentMethods.map((item) => (
                          <Col key={item} xs={24} md={24}>
                            <Button
                              type="default"
                              style={{
                                background: item === paymentMethod ? btnColour : '#ffffff',
                                color: item !== paymentMethod ? btnColour : '#ffffff',
                                border: `1px solid ${btnColour}`,
                                height: '50px',
                                fontWeight: '500',
                                fontSize: '16px',
                                width: '100%',
                                maxWidth: '420px',
                                margin: '0 auto',
                                display: 'block',
                              }}
                              onClick={() => {
                                installmentChoiceRef.current = item;
                                pendingSubmitTypeRef.current = 'installment';
                                setelectPaymentMethod(item);
                                setSubmitType('installment');
                              }}
                              htmlType="submit"
                            >
                              {renderPaymentMethodLabel(item, item === paymentMethod)}
                            </Button>
                          </Col>
                        ))}
                    </>
                  ) : (
                    prioritizedInstallmentMethods.map((item) => (
                      <Col key={item} xs={24} md={24}>
                        <Button
                          type="default"
                          style={{
                            background: item === paymentMethod ? btnColour : '#ffffff',
                            color: item !== paymentMethod ? btnColour : '#ffffff',
                            border: `1px solid ${btnColour}`,
                            height: '50px',
                            fontWeight: '500',
                            fontSize: '16px',
                            width: '100%',
                            maxWidth: '320px',
                            margin: '0 auto',
                            display: 'block',
                          }}
                          onClick={() => {
                            installmentChoiceRef.current = item;
                            pendingSubmitTypeRef.current = 'installment';
                            setelectPaymentMethod(item);
                            setSubmitType('installment');
                          }}
                          htmlType="submit"
                        >
                          {renderPaymentMethodLabel(item, item === paymentMethod)}
                        </Button>
                      </Col>
                    ))
                  )}
                </Row>
              </div>
            </div>

            {/* <Row gutter={24}>
              <Col xs={24} md={24} className="flex justify-between items-center pt-2">
                <Button
                  type="default"
                  style={{
                    border: 'none',
                    color: btnColour || '#ffffff',
                    height: '50px',
                    fontSize: '16px',
                    fontWeight: '600',
                    boxShadow: 'none',
                    background: 'transparent',
                  }}
                  className="flex align-items-center"
                  onClick={() => setPaymentType('one_time')}
                  htmlType="button" // Changed from "default" to "button"
                >
                  <UilArrowLeft className="mr-1" /> {language?.content?.BACK}
                </Button>
              </Col>
            </Row> */}
          </>
        </Form>
      ) : (
        ''
      )}
      {paymentType === 'subscription' && (
        <Form
          className="user-details-form"
          name="basic"
          layout="vertical"
          initialValues={{ remember: true }}
          onFinish={onFinish}
          onFinishFailed={onFinishFailed}
        >
          <>
            <Row gutter={24} className="mb-20">
              <Col xs={12} md={12}>
                <Button
                  type="default"
                  style={{
                    background: subscriptionType === 'month' ? btnColour : '#ffffff',
                    color: subscriptionType !== 'month' ? btnColour : '#ffffff',
                    border: subscriptionType === 'month' ? btnColour : '#ffffff',
                    height: '50px',
                    fontWeight: '500',
                    fontSize: '16px',
                  }}
                  block
                  onClick={() => setsubscriptionType('month')}
                >
                  {language?.content?.MONTHLY}
                </Button>
              </Col>
              <Col xs={12} md={12}>
                <Button
                  type="default"
                  style={{
                    background: subscriptionType === 'year' ? btnColour : '#ffffff',
                    color: subscriptionType !== 'year' ? btnColour : '#ffffff',
                    height: '50px',
                    fontWeight: '500',
                    fontSize: '16px',
                    border: subscriptionType === 'year' ? `1px solid ${btnColour}` : `1px solid ${btnColour}`,
                  }}
                  block
                  onClick={() => setsubscriptionType('year')}
                >
                  {language?.content?.YEARLY}
                </Button>
              </Col>
            </Row>
            <Row gutter={24}>
              <Col xs={24} md={24} className="custom-height">
                <Form.Item
                  label={language?.content?.DURATION}
                  name="duration"
                  rules={[{ required: true, message: language?.message?.error?.ERR056 }]}
                >
                  <InputNumber
                    placeholder={language?.content?.DURATION_PLACEHOLDER}
                    min={1}
                    maxLength={10}
                    step={1}
                    formatter={(value) => `${value}`}
                    parser={(value) => value.replace(/€\s?|(,*)/g, '')}
                    style={{ width: '100%' }}
                  />
                </Form.Item>
              </Col>
            </Row>
            <Row gutter={24}>
              <Col xs={24} md={24} className="flex justify-between items-center pt-2">
                <Button
                  type="default"
                  style={{
                    border: 'none',
                    color: btnColour || '#ffffff',
                    height: '50px',
                    fontSize: '16px',
                    fontWeight: '600',
                    boxShadow: 'none',
                    background: 'transparent',
                  }}
                  className="flex align-items-center"
                  onClick={() => setPaymentType('one_time')}
                  htmlType="button" // Changed from "default" to "button"
                >
                  <UilArrowLeft className="mr-1" /> {language?.content?.BACK}
                </Button>
                <Button
                  type="default"
                  block
                  style={{
                    borderColor: btnColour || '#fc8600',
                    color: btnColour || '#ffffff',
                    height: '50px',
                    width: '176px',
                    fontWeight: '500',
                    fontSize: '16px',
                  }}
                  onClick={() => {
                    pendingSubmitTypeRef.current = 'subscription';
                    setSubmitType('subscription');
                  }}
                  htmlType="submit"
                >
                  {language?.content?.SUBSCRIBE || 'Subscribe'}
                </Button>
              </Col>
            </Row>
          </>
        </Form>
      )}
    </>
  );
}

export default PaymentForm;
